import { z } from 'zod';
import { FlexVisibility } from '../services/flex-period.service';
import { ColorProfile } from './ColorProfile';
import { Location } from './Location';

export const SchoolActivityStateSchema = z.enum(['flex_recurring', 'scheduled', 'canceled', 'deleted', 'archived']);
export const SchoolActivityStatusSchema = z.enum(['active', 'archived']);
export const SchoolActivityScheduleTypeSchema = z.enum(['flex_period', 'custom']);

/*
 * Setting type to 'any' on managers as a trade off to avoid circular dependency with User.ts.
 * Initially tried using z.lazy(() => User.schema.array()), but this caused a type error on the
 * SchoolActivitySchema and setting a ZodType on the schema meant that SchoolActivity was returning a type of any.
 */
export const SchoolActivitySchema = z.object({
	id: z.number().int(),
	icon: z.string(),
	name: z.string(),
	description: z.string(),
	user_id: z.number().int(),
	location_id: z.number().int(),
	color_profile: ColorProfile.schema.optional(),
	flex_period_id: z.number().int(),
	max_attendees: z.number().int(),
	public_event: z.boolean(),

	teacher_name: z.string().optional(),
	profile_picture: z.string().optional(),

	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	deleted_at: z.string().optional(),
	deleted_by: z.number().int().optional(),
	deleted_by_name: z.string().optional(),
	archived_by: z.number().int().optional(),
	archived_by_name: z.string().optional(),

	state: SchoolActivityStateSchema,
	status: SchoolActivityStatusSchema,

	managers: z.any().array(),
	schedule_type: SchoolActivityScheduleTypeSchema,

	repeat_end_date: z.string().optional(),
	custom_start_time: z.string().optional(),
	custom_end_time: z.string().optional(),
	custom_repeat_rule: z.string().optional(),

	visibility: z.nativeEnum(FlexVisibility).optional(),
	visibility_student_ids: z.number().int().array().optional(),
	visibility_group_ids: z.number().int().array().optional(),
});

export type SchoolActivityState = z.infer<typeof SchoolActivityStateSchema>;
export type SchoolActivityStatus = z.infer<typeof SchoolActivityStatusSchema>;
export type SchoolActivityScheduleType = z.infer<typeof SchoolActivityScheduleTypeSchema>;
export type SchoolActivity = z.infer<typeof SchoolActivitySchema>;
export interface SchoolActivityRow extends SchoolActivity {
	location_icon: string;
	location_name: string;
	location?: Location;
	flex_period_name: string;
}

export interface SchoolActivityInstance {
	id: number;
	start_time: string;
	end_time: string;
	activity_id: number;
	created_at?: string;
	updated_at?: string;
	current_num_attendees?: number;
	color_profile?: ColorProfile;
	selected?: boolean;
	flex_period_id?: number;
	activity_name?: string;
	schedule_type?: SchoolActivityScheduleType;
}
